import React, {useEffect} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowLeft, faBoxOpen, faXmark} from "@fortawesome/free-solid-svg-icons";
import {useUserCatalog} from "../store/user";
import LearnerShell from "../components/learner/LearnerShell";
import ResourceCard from "../components/learner/ResourceCard";
import Paginator from "../components/learner/Paginator";
import EmptyState from "../components/learner/EmptyState";
import CartToast from "../components/landing/CartToast";
import {imageUrl} from "../lib/format";

const LIMIT = 24;

// The catalog API does not return rating / reviews / hours / oldPrice yet.
// Stable per-id mocks keep the UI consistent across re-renders until the
// backend ships those fields.
const hashOf = (id) => {
  const s = String(id);
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
};
const mockRating = (id) => 3.8 + (hashOf(id) % 12) / 10;
const mockReviews = (id) => 15 + (hashOf(id) % 480);
const mockHours = (id) => 2 + (hashOf(id) % 10);
const mockOldPrice = (price) => {
  const p = Number(price) || 0;
  if (p <= 0) return null;
  return Math.round((p * 1.3) / 50) * 50;
};
const mockFlags = (id) => {
  const h = hashOf(id);
  return {bestseller: h % 5 === 0, hot: h % 7 === 0};
};
const levelLabel = (value) => {
  const s = String(value || "").toLowerCase();
  if (!s) return "All Levels";
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
};

/**
 * /courses — browse the full approved-course catalog, filterable by category.
 *
 *   GET  /api/learners/courses?page&limit&search&sort&categoryId&isFeatured
 *   GET  /api/learners/categories
 *   POST /api/learners/courses/:id/purchase
 *
 * Unlike GET /api/learners/courses/enrolled, this endpoint accepts `search`
 * and `categoryId` as query params (strict schema), so filtering is server-side.
 */
const AllCourses = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    userCourses,
    userCoursesLoading,
    userCoursesTotal,
    userCoursesPages,
    userCoursesError,
    fetchUserCourses,
    userCategories,
    fetchUserCategories,
  } = useUserCatalog();

  const page = Math.max(1, Number(searchParams.get("page")) || 1);
  const search = searchParams.get("search") || "";
  const categoryId = searchParams.get("categoryId") || "";

  // GET /api/learners/categories — powers the category chip filter.
  useEffect(() => {
    fetchUserCategories();
  }, [fetchUserCategories]);

  // GET /api/learners/courses?page&limit&search&sort&categoryId
  useEffect(() => {
    fetchUserCourses({
      page,
      limit: LIMIT,
      sort: "newest",
      ...(search ? {search} : {}),
      ...(categoryId ? {categoryId} : {}),
    });
  }, [fetchUserCourses, page, search, categoryId]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== "page") next.delete("page");
    setSearchParams(next, {replace: true});
  };

  const courseIdOf = (course) => course?.id ?? course?.courseId;

  // Catalog tutors arrive as {name} objects — never render the raw object.
  const instructorNameOf = (course) =>
    course?.tutor?.name ||
    course?.tutor?.fullName ||
    (typeof course?.instructor === "string"
      ? course.instructor
      : course?.instructor?.name) ||
    "";

  const openCourse = (course) => navigate(`/content-library/course/${courseIdOf(course)}`);

  return (
    <LearnerShell onSearchChange={(term) => setParam("search", term)}>
      <CartToast />

      <button
        type="button"
        onClick={() => navigate("/")}
        className="mb-4 inline-flex items-center gap-2 text-sm font-semibold text-slate-600 transition-colors hover:text-blue-800"
      >
        <FontAwesomeIcon icon={faArrowLeft} className="text-xs" />
        Back to home
      </button>

      {/* Category filter — mirrors the "Discover Your Next Skill" chip bar. */}
      <div
        className="mb-5 flex gap-2 overflow-x-auto border-b border-slate-200 pb-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        role="tablist"
        aria-label="Course categories"
      >
        {[{id: "", name: "All"}, ...userCategories].map((category) => {
          const active = categoryId === category.id;
          return (
            <button
              key={category.id || "all"}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setParam("categoryId", category.id)}
              className={`shrink-0 rounded-full border px-4 py-2 text-sm font-medium transition-all ${
                active
                  ? "border-blue-900 bg-blue-900 text-white"
                  : "border-slate-200 bg-white text-slate-600 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-800"
              }`}
            >
              {category.name}
            </button>
          );
        })}
      </div>

      {search || userCoursesError ? (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {search ? (
            <button
              type="button"
              onClick={() => setParam("search", "")}
              className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-200"
            >
              <FontAwesomeIcon icon={faXmark} />
              Search “{search}”
            </button>
          ) : null}
          {userCoursesError ? (
            <p className="m-0 rounded-lg bg-amber-50 px-3 py-2 text-xs font-medium text-amber-700">
              {userCoursesError}
            </p>
          ) : null}
        </div>
      ) : null}

      <p className="m-0 mb-3 text-xs font-medium uppercase tracking-wide text-slate-500">
        {`${userCoursesTotal} course${userCoursesTotal === 1 ? "" : "s"} available`}
      </p>

      {userCoursesLoading && userCourses.length === 0 ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {[0, 1, 2, 3, 4, 5, 6, 7].map((index) => (
            <div key={index} className="h-80 animate-pulse rounded-2xl border border-slate-200 bg-white" />
          ))}
        </div>
      ) : userCourses.length > 0 ? (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {userCourses.map((course) => {
              const id = courseIdOf(course);
              const flags = mockFlags(id);
              return (
                <ResourceCard
                  key={id}
                  courseId={id}
                  image={imageUrl(course?.coverImage)}
                  title={course?.title}
                  subtitle={instructorNameOf(course)}
                  badge={course?.category?.name || "Course"}
                  rating={course?.rating != null ? course.rating : mockRating(id)}
                  reviews={course?.reviews != null ? course.reviews : mockReviews(id)}
                  hours={course?.hours != null ? course.hours : mockHours(id)}
                  level={levelLabel(course?.difficultyLevel)}
                  oldPrice={course?.oldPrice != null ? course.oldPrice : mockOldPrice(course?.price)}
                  bestseller={flags.bestseller}
                  hot={flags.hot}
                  price={course?.price}
                  isFree={Number(course?.price) === 0}
                  onOpen={() => openCourse(course)}
                />
              );
            })}
          </div>
          <Paginator
            page={page}
            totalPages={userCoursesPages}
            onChange={(next) => setParam("page", String(next))}
          />
        </>
      ) : (
        <EmptyState
          icon={faBoxOpen}
          title="No courses found"
          hint={
            search || categoryId
              ? "Try a different search term or topic."
              : "Approved courses will appear here as soon as they are published."
          }
        />
      )}
    </LearnerShell>
  );
};

export default AllCourses;
